import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import Button from "@/components/Button";
import DetailsCard, { type SelectOptions } from "@/components/DetailsCard/DetailsCard";
import ListItem from "@/components/ListItem";
import { CardSkeleton } from "@/components/Skeleton";
import { formatDate } from "@/pages/_layouts/jobs/_utils";

function HubViewFooter({ viewAllHref, title, testId }: { viewAllHref: string; title: string; testId: string }) {
  const navigate = useNavigate();

  return (
    <div
      className="-mx-6 -mb-6 mt-auto flex w-[calc(100%+3rem)] items-center justify-center border-t border-sys-brand-surface-container-highest py-2"
      data-testid={`${testId}-view-footer`}
    >
      <Button
        variant="text"
        testId={`${testId}-view-all`}
        ariaLabel={`View ${title}`}
        onClick={() => navigate(viewAllHref)}
      >
        View
      </Button>
    </div>
  );
}

export default function CareerPlanSummaryCard({
  title,
  testId,
  updatedAt,
  loading,
  viewAllHref,
  showViewAll = true,
  options = [],
  children
}: {
  title: string;
  testId: string;
  updatedAt?: string | null;
  loading: boolean;
  viewAllHref?: string;
  showViewAll?: boolean;
  options?: SelectOptions[];
  children?: ReactNode;
}) {
  const updatedLabel = updatedAt ? `Updated ${formatDate(updatedAt)}` : null;

  return (
    <div className="h-full min-w-0 w-full" data-testid={testId}>
      {loading ? (
        <CardSkeleton className="w-full" width={100} rows={5} />
      ) : (
        <DetailsCard
          heading={title}
          className="!items-stretch"
          actionButtons={
            showViewAll && viewAllHref ? (
              <HubViewFooter viewAllHref={viewAllHref} title={title} testId={testId} />
            ) : undefined
          }
        >
          <div className="flex min-h-0 w-full flex-1 flex-col">
            {updatedLabel ? (
              <p
                className="-mt-1 mb-4 text-body-medium text-sys-brand-on-surface-variant"
                data-testid="details-card-caption"
              >
                {updatedLabel}
              </p>
            ) : null}
            {options.length > 0 ? (
              <div className="flex w-full min-w-0 max-w-full flex-col">
                {options.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <ListItem {...item} iconBackground />
                  </div>
                ))}
              </div>
            ) : null}
            {children}
          </div>
        </DetailsCard>
      )}
    </div>
  );
}
